// Types + helpers for the Manual Booking admin screen (SMB-3541).
//
// Manual Booking records a flight/hotel reservation that was ALREADY booked and
// paid for outside Zenvoya (customer paid ops; ops booked with the supplier).
// No search, no availability, no payment — the payload is persisted directly as a
// confirmed / paid booking. See the SMB-3541 hand-off doc for the full contract.
//
// Conventions mirror src/components/promo-code/types.ts: every form field is a
// string/bool so inputs stay controlled; empty*() factories seed new rows;
// validate*() returns path-keyed FieldErrors; build*Payload() coerces to the API
// shape (stripping empty optionals). Server re-validates everything.

//********TYPES STARTS*********/
export type PaxType = 'ADT' | 'CHD' | 'INF';
export type AirlineType = 'GDS' | 'LCC';
export type BoardType = '' | 'RO' | 'BB' | 'HB' | 'FB' | 'AI';
export type TripType = '' | '1' | '2' | '3';
export type PaymentType = '' | 'CASH' | 'CARD' | 'BANK_TRANSFER' | 'OFFLINE';

// Path-keyed errors, e.g. "amounts.totalUsrAmt", "sectors.0.pnr",
// "sectors.0.segments.1.flightNo", "passengers.0.email". "_leadPax" / "_form" are
// form-level keys not tied to a single input.
export type FieldErrors = Record<string, string>;

export interface AmountsForm {
  totalUsrAmt: string;
  supTotalAmt: string;
  usCurTotalAmt: string;
  currency: string;
  productPrice: string; // maps to flightPrice / hotelPrice (optional)
}

// How the customer paid ops — written to payment_details (§5b). Card fields are
// only sent for CARD and must be masked / last-4 (never the full PAN).
export interface PaymentForm {
  paymentType: PaymentType;
  customerName: string;
  cardType: string;
  cardNumber: string;
  cardExpiry: string; // YYYY-MM-DD
  remarks: string;
}

export interface OperationsForm {
  totalFare: string;
  baseFare: string;
  supplierTotalFare: string;
  supplierBaseFare: string;
  markupAmount: string;
  servicefeeAmount: string;
}

export interface DocumentForm {
  type: string;
  number: string;
  nationality: string;
  issuedCountry: string;
  issuedDate: string; // YYYY-MM-DD
  expiryDate: string; // YYYY-MM-DD
}

export interface SegmentForm {
  segSource: string;
  segDestination: string;
  marketingAirline: string;
  operatingAirline: string;
  flightNo: string;
  departTime: string; // datetime-local "YYYY-MM-DDTHH:mm"
  arrivalTime: string;
  segClass: string;
  departTerminal: string;
  arrivalTerminal: string;
  airlinePnr: string;
  freeBaggage: string;
  cabinBaggage: string;
}

export interface SectorForm {
  source: string;
  destination: string;
  pnr: string;
  cabinClass: string;
  journeyType: string; // '' | 'onward' | 'return' | 'Onward'
  airlineType: AirlineType;
  isRefundable: boolean;
  supBaseFare: string;
  supTaxes: string;
  supTotalFare: string;
  segments: SegmentForm[];
}

export interface InfantForm {
  title: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string; // YYYY-MM-DD
  hasDocument: boolean;
  document: DocumentForm;
  operations: OperationsForm;
}

export interface PassengerForm {
  paxType: PaxType;
  title: string;
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string; // YYYY-MM-DD
  leadPax: boolean;
  email: string;
  countryCode: string;
  phoneNumber: string;
  countryName: string;
  iso: string;
  hasDocument: boolean;
  document: DocumentForm;
  hasOperations: boolean;
  operations: OperationsForm;
  hasInfant: boolean;
  infant: InfantForm;
}

export interface FlightForm {
  tripType: TripType;
  amounts: AmountsForm;
  sectors: SectorForm[];
  passengers: PassengerForm[];
}

export interface HotelForm {
  supplier: string;
  hotelName: string;
  city: string;
  country: string;
  checkIn: string; // YYYY-MM-DD
  checkOut: string; // YYYY-MM-DD
  bookingReference: string;
  roomName: string;
  roomId: string;
  noOfRooms: string;
  adultCount: string;
  childCount: string;
  boardType: BoardType;
  isRefundable: boolean;
  supBaseFare: string;
  supTaxes: string;
  supTotalFare: string;
  markupAmount: string;
  servicefeeAmount: string;
  cancellationPolicy: string;
  roomsOccupancy: string;
}

export interface GuestForm {
  paxType: PaxType;
  title: string;
  firstName: string;
  middleName: string;
  lastName: string;
  roomNo: string;
  leadPax: boolean;
  email: string;
  countryCode: string;
  phoneNumber: string;
  countryName: string;
  iso: string;
}

export interface HotelBookingForm {
  amounts: AmountsForm;
  hotels: HotelForm[];
  guests: GuestForm[];
}

// Shared across both forms — collected once at the top of the page.
export interface BookingMeta {
  userId: string;
  createdBy: string;
  sendMail: boolean; // notify the ops agent by email (sent as `sendmail`)
  payment: PaymentForm; // how the customer paid ops (sent as top-level `payment`)
}

// Response envelope (post-unwrap). Success `data` carries the ids to display.
export interface ManualBookingResult {
  itineraryID?: string;
  transactionId?: string;
  tid?: string;
  bookingType?: string;
  paymentBreakdownId?: string;
  sqsEventPublished?: boolean;
  emailSent?: boolean;
}
export interface ManualBookingEnvelope {
  status: number;
  message?: string;
  data?: ManualBookingResult;
}
//********TYPES ENDS*********/

//********CONSTANTS STARTS*********/
export const PAX_TYPES: PaxType[] = ['ADT', 'CHD', 'INF'];
export const AIRLINE_TYPES: AirlineType[] = ['GDS', 'LCC'];
export const TITLES = ['Mr', 'Ms', 'Mrs', 'Mstr', 'Miss', 'Dr'];
export const BOARD_TYPES: { value: BoardType; label: string }[] = [
  { value: '', label: 'NA (none)' },
  { value: 'RO', label: 'RO — Room only' },
  { value: 'BB', label: 'BB — Bed & breakfast' },
  { value: 'HB', label: 'HB — Half board' },
  { value: 'FB', label: 'FB — Full board' },
  { value: 'AI', label: 'AI — All inclusive' },
];
export const JOURNEY_TYPES = ['', 'onward', 'return', 'Onward'];
export const PAYMENT_TYPES: { value: PaymentType; label: string }[] = [
  { value: '', label: 'Select payment type' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CARD', label: 'Card' },
  { value: 'BANK_TRANSFER', label: 'Bank transfer' },
  { value: 'OFFLINE', label: 'Offline / other' },
];
//********CONSTANTS ENDS*********/

//********FACTORIES STARTS*********/
export const emptyAmounts = (): AmountsForm => ({
  totalUsrAmt: '',
  supTotalAmt: '',
  usCurTotalAmt: '',
  currency: 'USD',
  productPrice: '',
});

export const emptyPayment = (): PaymentForm => ({
  paymentType: '',
  customerName: '',
  cardType: '',
  cardNumber: '',
  cardExpiry: '',
  remarks: '',
});

export const emptyOperations = (): OperationsForm => ({
  totalFare: '',
  baseFare: '',
  supplierTotalFare: '',
  supplierBaseFare: '',
  markupAmount: '',
  servicefeeAmount: '',
});

export const emptyDocument = (): DocumentForm => ({
  type: '',
  number: '',
  nationality: '',
  issuedCountry: '',
  issuedDate: '',
  expiryDate: '',
});

export const emptySegment = (): SegmentForm => ({
  segSource: '',
  segDestination: '',
  marketingAirline: '',
  operatingAirline: '',
  flightNo: '',
  departTime: '',
  arrivalTime: '',
  segClass: '',
  departTerminal: '',
  arrivalTerminal: '',
  airlinePnr: '',
  freeBaggage: '',
  cabinBaggage: '',
});

export const emptySector = (): SectorForm => ({
  source: '',
  destination: '',
  pnr: '',
  cabinClass: 'ECONOMY',
  journeyType: '',
  airlineType: 'GDS',
  isRefundable: false,
  supBaseFare: '',
  supTaxes: '',
  supTotalFare: '',
  segments: [emptySegment()],
});

export const emptyInfant = (): InfantForm => ({
  title: '',
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  hasDocument: false,
  document: emptyDocument(),
  operations: emptyOperations(),
});

export const emptyPassenger = (leadPax = false): PassengerForm => ({
  paxType: 'ADT',
  title: '',
  firstName: '',
  middleName: '',
  lastName: '',
  dateOfBirth: '',
  leadPax,
  email: '',
  countryCode: '',
  phoneNumber: '',
  countryName: '',
  iso: '',
  hasDocument: false,
  document: emptyDocument(),
  hasOperations: false,
  operations: emptyOperations(),
  hasInfant: false,
  infant: emptyInfant(),
});

export const emptyFlightForm = (): FlightForm => ({
  tripType: '',
  amounts: emptyAmounts(),
  sectors: [emptySector()],
  passengers: [emptyPassenger(true)],
});

export const emptyHotel = (): HotelForm => ({
  supplier: '',
  hotelName: '',
  city: '',
  country: '',
  checkIn: '',
  checkOut: '',
  bookingReference: '',
  roomName: '',
  roomId: '',
  noOfRooms: '1',
  adultCount: '',
  childCount: '',
  boardType: '',
  isRefundable: false,
  supBaseFare: '',
  supTaxes: '',
  supTotalFare: '',
  markupAmount: '',
  servicefeeAmount: '',
  cancellationPolicy: '',
  roomsOccupancy: '',
});

export const emptyGuest = (leadPax = false): GuestForm => ({
  paxType: 'ADT',
  title: '',
  firstName: '',
  middleName: '',
  lastName: '',
  roomNo: '1',
  leadPax,
  email: '',
  countryCode: '',
  phoneNumber: '',
  countryName: '',
  iso: '',
});

export const emptyHotelForm = (): HotelBookingForm => ({
  amounts: emptyAmounts(),
  hotels: [emptyHotel()],
  guests: [emptyGuest(true)],
});
//********FACTORIES ENDS*********/

//********NUMERIC / DATE HELPERS STARTS*********/
const isString = (x: unknown): x is string => typeof x === 'string';

const isBlank = (s: string) => s.trim() === '';

// present positive number (> 0)
const isPositive = (s: string) => {
  const n = Number(s);
  return !isBlank(s) && !Number.isNaN(n) && n > 0;
};
// present non-negative number (>= 0)
const isNonNeg = (s: string) => {
  const n = Number(s);
  return !isBlank(s) && !Number.isNaN(n) && n >= 0;
};
// present positive integer
const isPosInt = (s: string) => {
  const n = Number(s);
  return !isBlank(s) && Number.isInteger(n) && n > 0;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// datetime-local "YYYY-MM-DDTHH:mm" -> ISO-ish "YYYY-MM-DDTHH:mm:00"
export const toIsoDateTime = (v: string): string => {
  if (!v) return '';
  return v.length === 16 ? `${v}:00` : v;
};
//********NUMERIC / DATE HELPERS ENDS*********/

//********VALIDATION STARTS*********/
const validateAmounts = (a: AmountsForm, e: FieldErrors, prefix = 'amounts') => {
  if (!isPositive(a.totalUsrAmt)) e[`${prefix}.totalUsrAmt`] = 'Must be greater than 0';
  if (!isPositive(a.usCurTotalAmt)) e[`${prefix}.usCurTotalAmt`] = 'Must be greater than 0';
  if (!isNonNeg(a.supTotalAmt)) e[`${prefix}.supTotalAmt`] = 'Must be 0 or more';
  if (isBlank(a.currency)) e[`${prefix}.currency`] = 'Currency is required';
  if (!isBlank(a.productPrice) && !isNonNeg(a.productPrice))
    e[`${prefix}.productPrice`] = 'Must be 0 or more';
};

// Payment is optional per the contract, but the ops UI always collects it so the
// booking shows on the MyTrips Payments tab — so paymentType is required here.
const validatePayment = (p: PaymentForm, e: FieldErrors) => {
  if (isBlank(p.paymentType)) e['payment.paymentType'] = 'Select how the customer paid';
  if (!isBlank(p.cardExpiry) && !DATE_RE.test(p.cardExpiry)) e['payment.cardExpiry'] = 'YYYY-MM-DD';
};

// Shared lead-pax rule: exactly one lead, and the lead needs contact details.
// Works for both passengers[] and guests[]. Returns nothing; mutates `e`.
const validateLead = (
  rows: Array<{ leadPax: boolean; email: string; countryCode: string; phoneNumber: string }>,
  e: FieldErrors,
  prefix: string
) => {
  const leadCount = rows.filter((r) => r.leadPax).length;
  if (leadCount !== 1) {
    e._leadPax =
      leadCount === 0
        ? 'Exactly one lead is required — mark one row as lead'
        : 'Only one row can be the lead';
  }
  rows.forEach((r, i) => {
    if (!r.leadPax) return;
    if (isBlank(r.email) || !EMAIL_RE.test(r.email)) e[`${prefix}.${i}.email`] = 'Valid lead email is required';
    if (isBlank(r.countryCode)) e[`${prefix}.${i}.countryCode`] = 'Required for lead';
    if (isBlank(r.phoneNumber)) e[`${prefix}.${i}.phoneNumber`] = 'Required for lead';
  });
};

const validateDocument = (d: DocumentForm, e: FieldErrors, prefix: string) => {
  if (isBlank(d.type)) e[`${prefix}.type`] = 'Document type is required';
  if (isBlank(d.number)) e[`${prefix}.number`] = 'Document number is required';
  if (!isBlank(d.issuedDate) && !DATE_RE.test(d.issuedDate)) e[`${prefix}.issuedDate`] = 'YYYY-MM-DD';
  if (!isBlank(d.expiryDate) && !DATE_RE.test(d.expiryDate)) e[`${prefix}.expiryDate`] = 'YYYY-MM-DD';
};

export const validateFlightForm = (form: FlightForm, meta: BookingMeta): FieldErrors => {
  const e: FieldErrors = {};

  if (isBlank(meta.userId)) e.userId = 'User ID is required';
  validateAmounts(form.amounts, e);
  validatePayment(meta.payment, e);

  if (form.sectors.length === 0) e._form = 'At least one sector is required';
  form.sectors.forEach((s, si) => {
    const sp = `sectors.${si}`;
    if (isBlank(s.source)) e[`${sp}.source`] = 'Required';
    if (isBlank(s.destination)) e[`${sp}.destination`] = 'Required';
    if (isBlank(s.pnr)) e[`${sp}.pnr`] = 'PNR is required';
    if (!isNonNeg(s.supBaseFare)) e[`${sp}.supBaseFare`] = '≥ 0';
    if (!isNonNeg(s.supTaxes)) e[`${sp}.supTaxes`] = '≥ 0';
    if (!isNonNeg(s.supTotalFare)) e[`${sp}.supTotalFare`] = '≥ 0';
    if (s.segments.length === 0) e[`${sp}.segments`] = 'At least one segment is required';
    s.segments.forEach((g, gi) => {
      const gp = `${sp}.segments.${gi}`;
      if (isBlank(g.segSource)) e[`${gp}.segSource`] = 'Required';
      if (isBlank(g.segDestination)) e[`${gp}.segDestination`] = 'Required';
      if (isBlank(g.marketingAirline)) e[`${gp}.marketingAirline`] = 'Required';
      if (isBlank(g.flightNo)) e[`${gp}.flightNo`] = 'Required';
      if (isBlank(g.departTime)) e[`${gp}.departTime`] = 'Required';
      if (isBlank(g.arrivalTime)) e[`${gp}.arrivalTime`] = 'Required';
    });
  });

  if (form.passengers.length === 0) e._form = 'At least one passenger is required';
  form.passengers.forEach((p, pi) => {
    const pp = `passengers.${pi}`;
    if (isBlank(p.title)) e[`${pp}.title`] = 'Required';
    if (isBlank(p.firstName)) e[`${pp}.firstName`] = 'Required';
    if (isBlank(p.lastName)) e[`${pp}.lastName`] = 'Required';
    if (isBlank(p.dateOfBirth)) e[`${pp}.dateOfBirth`] = 'Required';
    else if (!DATE_RE.test(p.dateOfBirth)) e[`${pp}.dateOfBirth`] = 'YYYY-MM-DD';
    // Per-pax total fare is mandatory — the engine's total_fare column is NOT NULL.
    if (!isPositive(p.operations.totalFare)) e[`${pp}.operations.totalFare`] = 'Total fare is required';
    if (!isBlank(p.operations.baseFare) && !isNonNeg(p.operations.baseFare))
      e[`${pp}.operations.baseFare`] = 'Must be 0 or more';
    if (p.hasDocument) validateDocument(p.document, e, `${pp}.document`);
    if (p.hasInfant) {
      const ip = `${pp}.infant`;
      if (isBlank(p.infant.title)) e[`${ip}.title`] = 'Required';
      if (isBlank(p.infant.firstName)) e[`${ip}.firstName`] = 'Required';
      if (isBlank(p.infant.lastName)) e[`${ip}.lastName`] = 'Required';
      if (isBlank(p.infant.dateOfBirth)) e[`${ip}.dateOfBirth`] = 'Required';
      else if (!DATE_RE.test(p.infant.dateOfBirth)) e[`${ip}.dateOfBirth`] = 'YYYY-MM-DD';
      if (p.infant.hasDocument) validateDocument(p.infant.document, e, `${ip}.document`);
    }
  });
  validateLead(form.passengers, e, 'passengers');

  return e;
};

export const validateHotelForm = (form: HotelBookingForm, meta: BookingMeta): FieldErrors => {
  const e: FieldErrors = {};

  if (isBlank(meta.userId)) e.userId = 'User ID is required';
  validateAmounts(form.amounts, e);
  validatePayment(meta.payment, e);

  if (form.hotels.length === 0) e._form = 'At least one hotel is required';
  form.hotels.forEach((h, hi) => {
    const hp = `hotels.${hi}`;
    if (isBlank(h.supplier)) e[`${hp}.supplier`] = 'Required';
    if (isBlank(h.hotelName)) e[`${hp}.hotelName`] = 'Required';
    if (isBlank(h.city)) e[`${hp}.city`] = 'Required';
    if (isBlank(h.country)) e[`${hp}.country`] = 'Required';
    if (isBlank(h.bookingReference)) e[`${hp}.bookingReference`] = 'Confirmation ref is required';
    if (isBlank(h.roomName)) e[`${hp}.roomName`] = 'Required';
    if (isBlank(h.checkIn)) e[`${hp}.checkIn`] = 'Required';
    else if (!DATE_RE.test(h.checkIn)) e[`${hp}.checkIn`] = 'YYYY-MM-DD';
    if (isBlank(h.checkOut)) e[`${hp}.checkOut`] = 'Required';
    else if (!DATE_RE.test(h.checkOut)) e[`${hp}.checkOut`] = 'YYYY-MM-DD';
    if (h.checkIn && h.checkOut && DATE_RE.test(h.checkIn) && DATE_RE.test(h.checkOut) && h.checkOut <= h.checkIn)
      e[`${hp}.checkOut`] = 'Check-out must be after check-in';
    if (!isPosInt(h.noOfRooms)) e[`${hp}.noOfRooms`] = 'Whole number > 0';
    if (!isNonNeg(h.supBaseFare)) e[`${hp}.supBaseFare`] = '≥ 0';
    if (!isNonNeg(h.supTaxes)) e[`${hp}.supTaxes`] = '≥ 0';
    if (!isNonNeg(h.supTotalFare)) e[`${hp}.supTotalFare`] = '≥ 0';
  });

  if (form.guests.length === 0) e._form = 'At least one guest is required';
  form.guests.forEach((g, gi) => {
    const gp = `guests.${gi}`;
    if (isBlank(g.title)) e[`${gp}.title`] = 'Required';
    if (isBlank(g.firstName)) e[`${gp}.firstName`] = 'Required';
    if (isBlank(g.lastName)) e[`${gp}.lastName`] = 'Required';
    if (isBlank(String(g.roomNo))) e[`${gp}.roomNo`] = 'Required';
  });
  validateLead(form.guests, e, 'guests');

  return e;
};
//********VALIDATION ENDS*********/

//********PAYLOAD BUILDERS STARTS*********/
const num = (s: string) => Number(s);
const numOr0 = (s: string) => (isBlank(s) ? 0 : Number(s));
// add key only when the string is present
const put = (obj: Record<string, unknown>, key: string, s: string, asNumber = false) => {
  if (isBlank(s)) return;
  obj[key] = asNumber ? num(s) : s.trim();
};

const buildAmounts = (a: AmountsForm, priceKey: 'flightPrice' | 'hotelPrice') => {
  const out: Record<string, unknown> = {
    totalUsrAmt: num(a.totalUsrAmt),
    supTotalAmt: num(a.supTotalAmt),
    usCurTotalAmt: num(a.usCurTotalAmt),
    currency: a.currency.trim(),
  };
  if (!isBlank(a.productPrice)) out[priceKey] = num(a.productPrice);
  return out;
};

// Build the top-level `payment` block (§5b). Card fields only for CARD, always
// masked/last-4. Returns undefined when no payment type is chosen (nothing sent).
const buildPayment = (p: PaymentForm): Record<string, unknown> | undefined => {
  if (isBlank(p.paymentType)) return undefined;
  const out: Record<string, unknown> = { paymentType: p.paymentType };
  put(out, 'customerName', p.customerName);
  if (p.paymentType === 'CARD') {
    put(out, 'cardType', p.cardType);
    put(out, 'cardNumber', p.cardNumber);
    put(out, 'cardExpiry', p.cardExpiry);
  }
  put(out, 'remarks', p.remarks);
  return out;
};

// Per-pax fares. The engine columns (total_fare, base_fare, ...) are NOT NULL, so
// every field is always sent as a number (blank -> 0). baseFare falls back to
// totalFare when left empty.
const buildOperations = (o: OperationsForm) => ({
  totalFare: numOr0(o.totalFare),
  baseFare: isBlank(o.baseFare) ? numOr0(o.totalFare) : num(o.baseFare),
  supplierTotalFare: numOr0(o.supplierTotalFare),
  supplierBaseFare: numOr0(o.supplierBaseFare),
  markupAmount: numOr0(o.markupAmount),
  servicefeeAmount: numOr0(o.servicefeeAmount),
});

const buildDocument = (d: DocumentForm) => {
  const out: Record<string, unknown> = {};
  put(out, 'type', d.type);
  put(out, 'number', d.number);
  put(out, 'nationality', d.nationality);
  put(out, 'issuedCountry', d.issuedCountry);
  put(out, 'issuedDate', d.issuedDate);
  put(out, 'expiryDate', d.expiryDate);
  return out;
};

const buildSegment = (g: SegmentForm) => {
  const out: Record<string, unknown> = {
    segSource: g.segSource.trim().toUpperCase(),
    segDestination: g.segDestination.trim().toUpperCase(),
    marketingAirline: g.marketingAirline.trim(),
    flightNo: g.flightNo.trim(),
    departTime: toIsoDateTime(g.departTime),
    arrivalTime: toIsoDateTime(g.arrivalTime),
  };
  put(out, 'operatingAirline', g.operatingAirline);
  put(out, 'segClass', g.segClass);
  put(out, 'departTerminal', g.departTerminal);
  put(out, 'arrivalTerminal', g.arrivalTerminal);
  put(out, 'airlinePnr', g.airlinePnr);
  put(out, 'freeBaggage', g.freeBaggage);
  put(out, 'cabinBaggage', g.cabinBaggage);
  return out;
};

const buildSector = (s: SectorForm) => {
  const out: Record<string, unknown> = {
    source: s.source.trim().toUpperCase(),
    destination: s.destination.trim().toUpperCase(),
    pnr: s.pnr.trim(),
    supBaseFare: num(s.supBaseFare),
    supTaxes: num(s.supTaxes),
    supTotalFare: num(s.supTotalFare),
    airlineType: s.airlineType,
    isRefundable: s.isRefundable,
    segments: s.segments.map(buildSegment),
  };
  put(out, 'cabinClass', s.cabinClass);
  put(out, 'journeyType', s.journeyType);
  return out;
};

const buildPassenger = (p: PassengerForm) => {
  const out: Record<string, unknown> = {
    paxType: p.paxType,
    title: p.title.trim(),
    firstName: p.firstName.trim(),
    lastName: p.lastName.trim(),
    dateOfBirth: p.dateOfBirth,
    leadPax: p.leadPax,
  };
  put(out, 'middleName', p.middleName);
  put(out, 'countryName', p.countryName);
  put(out, 'iso', p.iso);
  if (p.leadPax) {
    out.email = p.email.trim();
    out.countryCode = p.countryCode.trim();
    out.phoneNumber = p.phoneNumber.trim();
  }
  if (p.hasDocument) {
    const doc = buildDocument(p.document);
    if (Object.keys(doc).length) out.document = doc;
  }
  // Always send operations — the engine's per-pax fare columns are NOT NULL.
  out.operations = buildOperations(p.operations);
  if (p.hasInfant) {
    const inf: Record<string, unknown> = {
      title: p.infant.title.trim(),
      firstName: p.infant.firstName.trim(),
      lastName: p.infant.lastName.trim(),
      dateOfBirth: p.infant.dateOfBirth,
      operations: buildOperations(p.infant.operations),
    };
    if (p.infant.hasDocument) {
      const doc = buildDocument(p.infant.document);
      if (Object.keys(doc).length) inf.document = doc;
    }
    out.infantData = inf;
  }
  return out;
};

// tripType defaults from sector count when not explicitly chosen (per doc §4.1).
const defaultTripType = (sectorCount: number): number => {
  if (sectorCount <= 1) return 1;
  if (sectorCount === 2) return 2;
  return 3;
};

export const buildFlightPayload = (form: FlightForm, meta: BookingMeta) => {
  const payload: Record<string, unknown> = {
    userId: meta.userId.trim(),
    createdBy: meta.createdBy.trim() || 'admin',
    sendmail: meta.sendMail,
    tripType: form.tripType ? Number(form.tripType) : defaultTripType(form.sectors.length),
    amounts: buildAmounts(form.amounts, 'flightPrice'),
    sectors: form.sectors.map(buildSector),
    passengers: form.passengers.map(buildPassenger),
  };
  const payment = buildPayment(meta.payment);
  if (payment) payload.payment = payment;
  return payload;
};

const buildHotel = (h: HotelForm) => {
  const out: Record<string, unknown> = {
    supplier: h.supplier.trim(),
    hotelName: h.hotelName.trim(),
    city: h.city.trim(),
    country: h.country.trim(),
    checkIn: h.checkIn,
    checkOut: h.checkOut,
    bookingReference: h.bookingReference.trim(),
    roomName: h.roomName.trim(),
    noOfRooms: num(h.noOfRooms),
    supBaseFare: num(h.supBaseFare),
    supTaxes: num(h.supTaxes),
    supTotalFare: num(h.supTotalFare),
    isRefundable: h.isRefundable,
  };
  if (h.boardType) out.boardType = h.boardType;
  put(out, 'roomId', h.roomId);
  put(out, 'adultCount', h.adultCount, true);
  put(out, 'childCount', h.childCount, true);
  put(out, 'markupAmount', h.markupAmount, true);
  put(out, 'servicefeeAmount', h.servicefeeAmount, true);
  put(out, 'cancellationPolicy', h.cancellationPolicy);
  put(out, 'roomsOccupancy', h.roomsOccupancy);
  return out;
};

const buildGuest = (g: GuestForm) => {
  const out: Record<string, unknown> = {
    paxType: g.paxType,
    title: g.title.trim(),
    firstName: g.firstName.trim(),
    lastName: g.lastName.trim(),
    roomNo: g.roomNo,
    leadPax: g.leadPax,
  };
  put(out, 'middleName', g.middleName);
  put(out, 'countryName', g.countryName);
  put(out, 'iso', g.iso);
  if (g.leadPax) {
    out.email = g.email.trim();
    out.countryCode = g.countryCode.trim();
    out.phoneNumber = g.phoneNumber.trim();
  }
  return out;
};

export const buildHotelPayload = (form: HotelBookingForm, meta: BookingMeta) => {
  const payload: Record<string, unknown> = {
    userId: meta.userId.trim(),
    createdBy: meta.createdBy.trim() || 'admin',
    sendmail: meta.sendMail,
    amounts: buildAmounts(form.amounts, 'hotelPrice'),
    hotels: form.hotels.map(buildHotel),
    guests: form.guests.map(buildGuest),
  };
  const payment = buildPayment(meta.payment);
  if (payment) payload.payment = payment;
  return payload;
};
//********PAYLOAD BUILDERS ENDS*********/

//********RESPONSE HELPERS STARTS*********/
// Manual-booking returns 201 on success (doc §6). Through the beadmin gateway the
// status may arrive as 200 — accept both.
export const isSuccessStatus = (status?: number): boolean => status === 201 || status === 200;

// Pull a human message + field-error strings out of a thrown/returned error.
// Robust to BOTH shapes: engine { status, error, message, details.validationErrors[] }
// and the gateway-wrapped { data: { status, message, data } } / data[] string arrays.
export const extractApiError = (err: any): { message: string; errors: string[] } => {
  const raw = err?.response?.data ?? err;
  const env = raw && raw.data && raw.data.status !== undefined ? raw.data : raw;
  const ve = env?.details?.validationErrors;
  let errors: string[] = [];
  if (Array.isArray(ve)) errors = ve.filter(isString);
  else if (Array.isArray(env?.data)) errors = (env.data as unknown[]).filter(isString);
  const message = env?.message || env?.error || err?.message || 'Something went wrong';
  return { message, errors };
};
//********RESPONSE HELPERS ENDS*********/
